import { logAmplitudeEvent } from 'amplitude/amplitude'
import { CustomFormAnyData } from 'customForm/types'
import {
  DecisionDisplayPropsType,
  MarketplaceProductDetailsType,
  openCreditDecisionDialog,
  setLightReachCap,
} from 'ducks/myEnergy'
import {
  ArrowLeftSmIcon,
  BaseThemeType,
  Button,
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Typography,
  useMediaQuery,
} from 'opensolar-ui'
import React, { useEffect, useMemo, useState } from 'react'
import { Form, useFormState } from 'react-final-form'
import { useDispatch } from 'react-redux'
import restClient from 'restClient'
import { ProjectType } from 'types/projects'
import { FormJSONType, HostedDisclosureType } from '../types'
import CreditAppicationDisclaimer from './CreditAppicationDisclaimer'
import CreditApplicationIntro from './CreditApplicationIntro'
import HostedCreditAppFormContent from './HostedCreditAppFormContent'
import { useInitialValuesByIntegration } from './initialValueSetters'
import LoadingOverlay from './LoadingOverlay'
import { useGetApplicationDisclosures } from './utils'
import VALIDATORS_BY_INTEGRATION from './validators'

const restClientInstance = restClient(window.API_ROOT + '/api')
const MAX_POLL_COUNT = 30

type PropTypes = {
  onClose: () => void
  orgId: number
  projectId: number
  systemUuid: string
  paymentOptionId: number
  integration: string
  setIsLoading: (val: boolean) => void
  isLoading: boolean
  project: ProjectType | null
  disclosures?: HostedDisclosureType[]
  initialValues: object
  isMarketplace?: boolean
  marketplaceProductDetails?: MarketplaceProductDetailsType
  formJSON?: FormJSONType | null
  logoURL?: string | null
  closeRootDialog?: () => void
}

const LogoWrapper = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
}))

const ActionsWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
}))

const ButtonsRow = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  justifyContent: 'flex-end',
}))

const ButtonWrapper = styled('div')(() => ({
  margin: '15px',
  display: 'flex',
  justifyContent: 'center',
}))

const MainContentWrapper = styled('div')(() => ({
  padding: '10px',
}))

const StyledDialog = styled(Dialog)<{ isMobile: boolean }>(({ isMobile }) => ({
  padding: isMobile ? '0px' : '32px',
}))

const StyledDialogTitle = styled(DialogTitle)({
  padding: '32px',
})

const StyledDialogContent = styled(DialogContent)<{ isMobile: boolean }>(({ isMobile }) => ({
  padding: '32px',
  minWidth: isMobile ? undefined : '600px',
}))

const ConfirmationDialogContent = styled(DialogContent)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '32px',
  marginTop: '32px',
}))

const ConfirmationDialogText = styled(Typography)(() => ({
  marginTop: '32px',
}))

const HostedCreditApplicationDialog: React.FC<PropTypes> = (props) => {
  const [errorMsg, setErrorMsg] = useState<React.ReactNode>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFetchingFormJSON, setIsFetchingFormJSON] = useState<boolean>(false)
  const [shouldPollForUpdate, setShouldPollForUpdate] = useState<boolean>(false)
  const [pollCount, setPollCount] = useState<number>(0)
  const [applicationIdToPoll, setApplicationIdToPoll] = useState<number | null>(null)
  const [jsonFormData, setJsonFormData] = useState<CustomFormAnyData>(props.initialValues as CustomFormAnyData)
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false)

  const dispatch = useDispatch()
  const formState = useFormState()
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))

  const {
    hostedDisclosures,
    logoURL: fetchedLogoURL,
    integrationDisplayName,
    formJSON: fetchedFormJSON,
  } = useGetApplicationDisclosures(
    props.orgId,
    props.projectId,
    props.systemUuid,
    props.paymentOptionId,
    props.disclosures,
    props.integration,
    setIsFetchingFormJSON
  )

  const formJSON = props.formJSON || fetchedFormJSON
  const logoURL = props.logoURL || fetchedLogoURL

  useEffect(() => {
    if (applicationIdToPoll && shouldPollForUpdate) {
      if (pollCount < MAX_POLL_COUNT) {
        // do polling
        restClientInstance('CUSTOM_GET', 'custom', {
          url: `orgs/${props.orgId}/projects/${props.projectId}/${props.integration}/application_status_and_details/?transaction_id=${applicationIdToPoll}`,
        })
          .then((res) => {
            if (res?.data?.poll_for_update) {
              setTimeout(() => {
                setPollCount(pollCount + 1)
              }, 5000)
            } else if (res?.data?.error_message) {
              setErrorMsg(res?.data?.error_message)
              setIsLoading(false)
              return
            } else if (res?.data?.decision) {
              logAmplitudeEvent('integrated_finance_application_submitted', {
                integration: props.integration,
                decision: res.data.decision,
                project_id: props.projectId,
                payment_option_id: props?.paymentOptionId,
              })
              const decisionDisplayProps = {
                decision: res.data.decision,
                stips: [], // not supported for integrations that require polling yet, only propensio needs this
                marketplaceProductDetails: props.marketplaceProductDetails,
              }
              dispatch(openCreditDecisionDialog(props.isMarketplace ? 'marketplace' : 'generic', decisionDisplayProps))
              if (props.closeRootDialog) props.closeRootDialog()
              props.onClose()
            }
          })
          .catch((err) => {
            setTimeout(() => {
              setPollCount(pollCount + 1)
            }, 5000)
          })
      } else {
        const decisionDisplayProps: DecisionDisplayPropsType = {
          decision: 'Pending',
          stips: [], // not supported for integrations that require polling yet, only propensio needs this
          marketplaceProductDetails: props.marketplaceProductDetails,
        }
        dispatch(openCreditDecisionDialog(props.isMarketplace ? 'marketplace' : 'generic', decisionDisplayProps))
        setIsLoading(false)
        if (props.closeRootDialog) props.closeRootDialog()
        props.onClose()
      }
    }
  }, [shouldPollForUpdate, pollCount, applicationIdToPoll, props.isMarketplace])

  useEffect(() => {
    if (props.isLoading && !!formJSON) {
      props.setIsLoading(false)
    }
  }, [formJSON, props.isLoading])

  const onSubmit = () => {
    let validator = VALIDATORS_BY_INTEGRATION[props.integration]
    if (validator) {
      const error = validator(formState.values)
      setErrorMsg(error)
      if (error) return
    }
    setIsLoading(true)
    setErrorMsg(null)

    const dataToSubmit = formJSON ? jsonFormData : formState.values
    restClientInstance('CUSTOM_POST', 'custom', {
      url: `orgs/${props.orgId}/projects/${props.projectId}/systems/${props.systemUuid}/${props.paymentOptionId}/${props.integration}/submit_application/`,
      data: dataToSubmit,
    })
      .then((res) => {
        if (res?.data?.poll_for_update) {
          setShouldPollForUpdate(true)
          setApplicationIdToPoll(res?.data?.credit_application_id)
          setPollCount(0)
        } else if (res?.data?.error_message) {
          setErrorMsg(res?.data?.error_message)
          setIsLoading(false)
          return
        } else if (res?.data?.decision) {
          setIsLoading(false)
          logAmplitudeEvent('integrated_finance_application_submitted', {
            integration: props.integration,
            decision: res.data.decision,
            project_id: props.projectId,
            payment_option_id: props?.paymentOptionId,
          })
          const decisionDisplayProps = {
            decision: res.data.decision,
            stips: res?.data?.stipulations || [],
            marketplaceProductDetails: props.marketplaceProductDetails,
          }
          dispatch(openCreditDecisionDialog(props.isMarketplace ? 'marketplace' : 'generic', decisionDisplayProps))
          props.onClose()
          if (props.closeRootDialog) props.closeRootDialog()
        }
      })
      .catch((err) => {
        let errorMessage = 'Something went wrong and we were unable to process your credit application'

        if (err?.body?.message) {
          errorMessage = err.body.message
        } else if (typeof err?.body === 'string') {
          errorMessage = err.body
        } else if (err?.message) {
          errorMessage = err.message
        }

        if (err?.body?.exceedsMonthlyPaymentCap) {
          dispatch(setLightReachCap(err.body.monthlyPaymentCap))

          setErrorMsg(
            'This application was not approved for the currently selected product due to a monthly payment cap. Please work with your sales representative on next steps.'
          )
        } else {
          setErrorMsg(errorMessage)
        }
        setIsLoading(false)
      })
  }

  const dialogTitle = useMemo(() => {
    if (integrationDisplayName) return `Apply for Financing with ${integrationDisplayName}`
    return 'Apply for Financing'
  }, [integrationDisplayName])

  if (props.isLoading) return null
  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <StyledDialog
        open={true}
        onClose={() => setShowConfirmationDialog(true)}
        fullScreen={isMobile}
        fullWidth={isMobile}
        isMobile={isMobile}
        maxWidth={isMobile ? undefined : 'lg'}
      >
        <StyledDialogTitle>
          <Typography textVariant="h5">{dialogTitle}</Typography>
        </StyledDialogTitle>
        <StyledDialogContent isMobile={isMobile}>
          <MainContentWrapper>
            {isLoading && <LoadingOverlay />}
            {logoURL && (
              <LogoWrapper>
                <img height="75px" src={logoURL}></img>
              </LogoWrapper>
            )}
            <CreditApplicationIntro integration={props.integration} />
            <HostedCreditAppFormContent
              onClose={props.onClose}
              integration={props.integration}
              disclosures={hostedDisclosures}
              form_json={formJSON}
              initialValues={props.initialValues}
              setJsonFormData={setJsonFormData}
              jsonFormData={jsonFormData}
            />
            <CreditAppicationDisclaimer integration={props.integration} />
          </MainContentWrapper>
        </StyledDialogContent>
        <DialogActions>
          <ActionsWrapper>
            {errorMsg && (
              <div>
                <Typography textVariant="body1" colorHex="#961616">
                  {errorMsg}
                </Typography>
              </div>
            )}
            <ButtonsRow>
              <ButtonWrapper>
                <Button variant="contained" onClick={() => setShowConfirmationDialog(true)}>
                  <ArrowLeftSmIcon /> Back
                </Button>
              </ButtonWrapper>
              <ButtonWrapper>
                <Button variant="contained" color="primary" onClick={onSubmit} disabled={isLoading}>
                  Submit Application
                </Button>
              </ButtonWrapper>
            </ButtonsRow>
          </ActionsWrapper>
        </DialogActions>
      </StyledDialog>
      <Dialog open={showConfirmationDialog} onClose={() => setShowConfirmationDialog(false)}>
        <DialogTitle>Exit Finance Application</DialogTitle>
        <ConfirmationDialogContent>
          {logoURL && <img src={logoURL} alt="Logo" height="50px" />}
          <ConfirmationDialogText textVariant="body1">
            Are you sure you want to exit your finance application? All Progress will be lost.
          </ConfirmationDialogText>
        </ConfirmationDialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmationDialog(false)}>Cancel</Button>
          <Button onClick={props.onClose}>Exit</Button>
        </DialogActions>
      </Dialog>
    </ComponentVersionsInherit>
  )
}

const FormWrappedDialog: React.FC<PropTypes> = (props) => {
  const validate = (formVals: object) => {
    let validator = VALIDATORS_BY_INTEGRATION[props.integration]
    if (validator) {
      const error = validator(formVals)
    }
  }

  const defaultInitialValues = useInitialValuesByIntegration(props.integration, props.project || null)
  const initialValues = props.initialValues || defaultInitialValues
  return (
    <Form
      onSubmit={validate}
      initialValues={initialValues}
      render={() => <HostedCreditApplicationDialog {...props} initialValues={initialValues} />}
    />
  )
}
export default FormWrappedDialog
